import React, { useEffect, useRef, useState } from 'react';
import MessageBubble from './MessageBubble';
import ThinkingBubble from './ThinkingBubble';
import { releasePin, rearmPin } from '../utils/scrollPin';
import styles from '../styles/ChatArea.module.css';

// Calendar-day identity of a message timestamp (viewer's local timezone);
// null when the timestamp is missing/unparseable so no separator is forced.
const dayKeyOf = (timestamp) => {
  if (!timestamp) return null;
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

// Human label for a day separator: Today / Yesterday / weekday within the
// last week / "March 4" (with year when it differs) beyond that.
// Locale is pinned to en-US: the app's UI copy is English-only, and the
// browser locale produced mixed-language labels ("6 de agosto" next to
// "Today") for non-English browsers.
const dayLabelOf = (timestamp) => {
  const d = new Date(timestamp);
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {})
  });
};

function ChatArea({ messages, isLoading, conversationId, currentMission, hasMoreMessages, isLoadingMore, onLoadMore, onPlanApprove, onPlanEdit, onArticleClick, onNewsClick, onCoachClick, onQuizComplete, onMessageRevealComplete, scrollToBottomTrigger, liveMessageIds }) {
  const chatAreaRef = useRef(null);
  const messagesContainerRef = useRef(null);
  // Floating jump-to-bottom button: shown once the user has scrolled up more
  // than a full page from the newest message.
  const [showJumpButton, setShowJumpButton] = useState(false);

  // Keep the newest message in view whenever the chat area resizes OR its
  // content grows. Resizes: opening the mobile keyboard collapses the top-bar
  // stack and shrinks the app shell at once, which used to scroll the tail of
  // the conversation out of sight. Content growth: a new reply landing, or a
  // message's word-by-word reveal, extends the messages container — observing
  // it keeps the bottom pinned so the latest answer stays visible. Only
  // re-pins if the user was already near the bottom; someone who has scrolled
  // up to read history is left where they are. The observer also fires once
  // on mount, which gives the initial jump-to-latest when history loads.
  // Pin/release rules — a proximity check alone ("near bottom = keep pinned")
  // traps the user: while content is still growing, every growth event snaps
  // the scroll back to the bottom before they can get far enough away from it.
  // Instead: ANY user scroll UP releases the pin immediately; deliberately
  // scrolling back down to the bottom re-arms it. Scroll events caused by our
  // own pinning are flagged and ignored so they can't re-arm/skew anything.
  useEffect(() => {
    const chatArea = chatAreaRef.current;
    const container = messagesContainerRef.current;
    if (!chatArea || typeof ResizeObserver === 'undefined') return undefined;

    const REPIN_PX = 40;
    let pinned = true;
    let programmatic = false;
    let lastScrollTop = chatArea.scrollTop;

    const pin = () => {
      const target = chatArea.scrollHeight - chatArea.clientHeight;
      // Skip a no-op assignment: it would fire no scroll event, leaving the
      // `programmatic` flag armed to swallow the user's next real scroll.
      if (Math.abs(target - chatArea.scrollTop) < 1) return;
      programmatic = true;
      chatArea.scrollTop = target;
    };

    // Show the jump-to-bottom button once the user is more than a full page
    // above the newest message (re-checked on scroll AND on content growth,
    // since a streaming reply can push the bottom away without any scrolling).
    const updateJumpButton = () => {
      const distanceFromBottom = chatArea.scrollHeight - chatArea.scrollTop - chatArea.clientHeight;
      setShowJumpButton(distanceFromBottom > chatArea.clientHeight);
    };

    const onScroll = () => {
      const top = chatArea.scrollTop;
      const distanceFromBottom = chatArea.scrollHeight - top - chatArea.clientHeight;
      // scrollTop also drops when the content shrinks (or the viewport grows)
      // and the browser clamps it — that lands exactly ON the bottom, whereas a
      // real scroll up always moves away from it. Don't mistake a clamp for her.
      const wentUp = top < lastScrollTop && distanceFromBottom > 1;
      lastScrollTop = top;
      updateJumpButton();
      if (programmatic) {
        programmatic = false;
        return;
      }
      if (wentUp) {
        pinned = false;
        // Publish it: stops the message-reveal auto-scrollers from dragging her
        // back down, and finishes any in-flight word-by-word reveal instantly.
        releasePin(chatArea);
      } else if (distanceFromBottom <= REPIN_PX) {
        pinned = true;
        rearmPin(chatArea);
      }
    };
    chatArea.addEventListener('scroll', onScroll, { passive: true });

    const observer = new ResizeObserver(() => {
      if (pinned) pin();
      updateJumpButton();
    });
    observer.observe(chatArea);
    if (container) observer.observe(container);

    return () => {
      chatArea.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, []);

  // Jump to the latest message when the user sends one (trigger from parent),
  // even if they had scrolled up into history.
  const prevTriggerRef = useRef(scrollToBottomTrigger);
  useEffect(() => {
    if (scrollToBottomTrigger !== prevTriggerRef.current) {
      prevTriggerRef.current = scrollToBottomTrigger;
      const chatArea = chatAreaRef.current;
      if (chatArea) chatArea.scrollTop = chatArea.scrollHeight;
    }
  }, [scrollToBottomTrigger]);

  const handleLoadMore = () => {
    if (isLoadingMore || !onLoadMore) return;
    onLoadMore();
  };

  // Smooth-glide back to the newest message. The pin logic re-arms itself on
  // the way down (any scroll that lands near the bottom re-pins).
  const jumpToBottom = () => {
    const chatArea = chatAreaRef.current;
    if (chatArea) {
      chatArea.scrollTo({ top: chatArea.scrollHeight, behavior: 'smooth' });
    }
  };

  // Get the last assistant message for the main display.
  // Skip empty-content messages (server occasionally saves assistant messages
  // containing only behavioral tags that strip to an empty string) and
  // synthetic "[...]" context messages.
  // If a USER message is the newest visible message (the optimistic echo while
  // "(thinking...)" is up), there is no main-display message — everything
  // renders as history so the conversation reads top-to-bottom in order.
  const getLastAssistantMessage = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      const text = msg.content?.trim();
      if (!text || text.startsWith('[')) continue;
      if (msg.role === 'user') return null;
      if (msg.role === 'assistant') return msg;
    }
    return null;
  };

  // Get history messages (all visible messages except the last assistant message)
  const getHistoryMessages = () => {
    if (messages.length === 0) return [];

    // Hide synthetic context messages (e.g. "[User just finished reading ...]"):
    // anything whose content starts with "[" is internal context for Sophia,
    // not a real chat message, so it must never appear in the history.
    const visible = messages.filter(
      msg => msg.content?.trim() && !msg.content.trim().startsWith('[')
    );
    const lastAssistant = getLastAssistantMessage();
    if (!lastAssistant) return visible;

    return visible.filter(msg => msg.id !== lastAssistant.id);
  };

  const lastAssistantMessage = getLastAssistantMessage();
  const historyMessages = getHistoryMessages();

  // For long conversations the server initially sends the first 5 + last 5
  // messages with a gap in between (hasMoreMessages). Split the history at
  // that gap so the spliced thread doesn't read as contiguous — the "show
  // full conversation" button sits exactly where the missing middle belongs,
  // and loading fills it in place (content inserts below the button, so the
  // browser keeps the reader's scroll position naturally).
  const headIds = hasMoreMessages
    ? new Set(messages.slice(0, 5).map(m => m.id))
    : null;
  const headHistory = headIds ? historyMessages.filter(m => headIds.has(m.id)) : [];
  const tailHistory = headIds ? historyMessages.filter(m => !headIds.has(m.id)) : historyMessages;

  // Day separators ("Today", "Yesterday", "Monday", "March 4") mark where a
  // day passed between messages — they replaced the old "Current conversation"
  // divider. `lastRenderedDayKey` accumulates across the render, in source
  // order (head history → tail history → main-display message), so each day is
  // labelled exactly once.
  let lastRenderedDayKey = null;
  const renderDaySeparator = (timestamp) => {
    const key = dayKeyOf(timestamp);
    if (!key || key === lastRenderedDayKey) return null;
    lastRenderedDayKey = key;
    return (
      <div className={styles.historySeparator}>
        <span>{dayLabelOf(timestamp)}</span>
      </div>
    );
  };

  const renderHistoryBubble = (message, index) => (
    <React.Fragment key={message.id || index}>
      {renderDaySeparator(message.timestamp)}
      <MessageBubble
        role={message.role}
        content={message.content}
        timestamp={message.timestamp}
        conversationId={conversationId}
        currentMission={currentMission}
        onPlanApprove={onPlanApprove}
        onPlanEdit={onPlanEdit}
        onArticleClick={onArticleClick}
        onNewsClick={onNewsClick}
        onCoachClick={onCoachClick}
        onQuizComplete={onQuizComplete}
        isHistory={true}
      />
    </React.Fragment>
  );

  return (
    <div className={styles.chatArea} ref={chatAreaRef}>
      <div className={styles.messagesContainer} ref={messagesContainerRef}>
        {/* Full conversation history, always visible above the current message */}
        {(historyMessages.length > 0 || hasMoreMessages) && (
          <div className={styles.historySection}>
            {headHistory.map(renderHistoryBubble)}
            {/* The not-yet-loaded middle of the conversation, one tap away —
                a button is reliable on every input device, unlike the old
                pull/drag gesture. */}
            {hasMoreMessages && (
              isLoadingMore ? (
                <div className={styles.loadingMore}>
                  <span>Loading history...</span>
                </div>
              ) : (
                <button type="button" className={styles.loadMoreButton} onClick={handleLoadMore}>
                  Show full conversation
                </button>
              )
            )}
            {tailHistory.map(renderHistoryBubble)}
          </div>
        )}

        {/* The current assistant message stays visible at all times — the wait
            is carried by the inline "thinking..." bubble below it, never by
            a screen takeover. */}
        <div>
          {/* Day separator for the current message too (skipped on a fresh
              conversation where the greeting is the only thing on screen —
              a lone "Today" above it is noise). */}
          {(historyMessages.length > 0 || hasMoreMessages) &&
            lastAssistantMessage &&
            renderDaySeparator(lastAssistantMessage.timestamp)}
          {lastAssistantMessage && (
            <MessageBubble
              key={lastAssistantMessage.id}
              role={lastAssistantMessage.role}
              content={lastAssistantMessage.content}
              timestamp={lastAssistantMessage.timestamp}
              conversationId={conversationId}
              currentMission={currentMission}
              onPlanApprove={onPlanApprove}
              onPlanEdit={onPlanEdit}
              onArticleClick={onArticleClick}
              onNewsClick={onNewsClick}
              onCoachClick={onCoachClick}
              onQuizComplete={onQuizComplete}
              onRevealComplete={() => onMessageRevealComplete && onMessageRevealComplete(lastAssistantMessage.id)}
              // Word-by-word reveal is for text the user has never seen —
              // messages that arrived live from the LLM this session (or a
              // greeting generated just now). History renders instantly.
              instantReveal={!(liveMessageIds && liveMessageIds.has(lastAssistantMessage.id))}
            />
          )}
        </div>

        {/* Inline wait state: "(thinking...)" animates as part of the chat
            while Sophia's reply is on its way. */}
        {isLoading && <ThinkingBubble />}

        {/* Spacer — grows with the suggestion chips so content never hides behind them */}
        <div className={styles.bottomSpacer}></div>
      </div>

      {/* Floating jump-to-bottom button — appears when scrolled up more than a
          page, floats above the input bar in the bottom-right corner. */}
      {showJumpButton && (
        <button
          type="button"
          className={styles.jumpToBottom}
          onClick={jumpToBottom}
          aria-label="Jump to the latest message"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="7 6 12 11 17 6" />
            <polyline points="7 13 12 18 17 13" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default ChatArea;
